import { useCallback, useEffect, useRef } from 'react';

/** DANI cuts a chat's title at 100 characters; a seeded chat's name follows suit. */
export const SEED_TITLE_MAX_LENGTH = 100;

interface SeedTitleConfig {
    currentConversationId: string | null;
    threadId: string | null;
    isLoading: boolean;
    renameConversation: (conversationId: string, title: string) => Promise<unknown>;
}

/**
 * Names a chat `seedConversation` opened, once DANI has answered its first
 * question (docs/pdlc/insights-dive-deep.md §7, Q4).
 *
 * DANI saves a pre-seeded chat as "[Copy] <first message>", the title its
 * Duplicate gives. An insight card's chat is not a copy, so it is renamed.
 * The rename waits for a thread id and the end of the answer: DANI writes the
 * row before it sends the thread id, and its later saves never touch the
 * title. Best effort, like the plan-template rename in `useChatPageCommon`:
 * if the reader leaves before the answer, or the rename fails, "[Copy]" stays.
 * Leaving drops the rename for good, so a later visit never renames over a
 * name the reader gave the chat in the meantime.
 *
 * Returns the setter `seedConversation` calls; a blank title leaves DANI's.
 */
export function useSeedTitle({
    currentConversationId,
    threadId,
    isLoading,
    renameConversation,
}: SeedTitleConfig) {
    const pendingRef = useRef<{ conversationId: string; title: string; opened: boolean } | null>(
        null,
    );

    useEffect(() => {
        const pending = pendingRef.current;
        if (!pending) return;
        if (pending.conversationId !== currentConversationId) {
            // Not open yet (the landing seeds before the provider sees the new
            // id), or the reader has left it.
            if (pending.opened) pendingRef.current = null;
            return;
        }
        pending.opened = true;
        if (!threadId || isLoading) return;
        pendingRef.current = null;
        void renameConversation(pending.conversationId, pending.title).catch(() => undefined);
    }, [currentConversationId, threadId, isLoading, renameConversation]);

    return useCallback((conversationId: string, title?: string) => {
        const name = title?.trim().slice(0, SEED_TITLE_MAX_LENGTH).trim() ?? '';
        pendingRef.current = name ? { conversationId, title: name, opened: false } : null;
    }, []);
}
